import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Home, Sparkles, Languages, WifiOff } from "lucide-react";
import { LANGUAGES, useLang } from "../i18n/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import AdminSettings from "./AdminSettings";
import InstallButton from "./InstallButton";
import useOnlineStatus from "../hooks/useOnlineStatus";

const LANG_SHORT = { en: "EN", hi: "HI", mr: "MR", te: "TE" };

export default function Layout({ children }) {
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const online = useOnlineStatus();

  return (
    <div className="min-h-screen flex flex-col">
      {!online && (
        <div
          data-testid="offline-banner"
          role="status"
          className="w-full bg-[#1E3F2D] text-white text-sm font-semibold px-4 py-2 flex items-center justify-center gap-2"
        >
          <WifiOff className="w-4 h-4" />
          <span>{t("offline_banner")}</span>
        </div>
      )}
      <header className="sticky top-0 z-40 border-b border-border bg-[#F7F4EB]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 px-3 sm:px-6 py-3 sm:py-4">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-2 sm:gap-3 group min-w-0"
          >
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-md flex-shrink-0">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.5} />
            </div>
            <div className="leading-tight min-w-0">
              <div className="font-bold text-base sm:text-xl text-foreground truncate">{t("app_name")}</div>
              <div className="text-xs text-muted-foreground hidden md:block truncate">{t("tagline")}</div>
            </div>
          </Link>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {!isHome && (
              <>
                <Button
                  data-testid="nav-back-btn"
                  variant="ghost"
                  size="lg"
                  onClick={() => navigate(-1)}
                  className="rounded-full h-10 sm:h-12 gap-2 px-2 sm:px-4"
                  aria-label={t("back")}
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="hidden sm:inline">{t("back")}</span>
                </Button>
                <Button
                  data-testid="nav-home-btn"
                  variant="ghost"
                  size="lg"
                  onClick={() => navigate("/")}
                  className="rounded-full h-10 sm:h-12 gap-2 px-2 sm:px-4"
                  aria-label={t("home")}
                >
                  <Home className="w-5 h-5" />
                  <span className="hidden sm:inline">{t("home")}</span>
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="lang-switcher-btn"
                  variant="outline"
                  size="lg"
                  className="rounded-full h-10 sm:h-12 px-3 sm:px-4 border-2 font-semibold bg-white gap-1.5 md:hidden"
                  aria-label={t("language")}
                >
                  <Languages className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">{LANG_SHORT[current.code]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-2 min-w-[160px]">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className="text-base py-3 cursor-pointer"
                  >
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Inline segmented language toggle (desktop / large tablets) */}
            <div
              role="tablist"
              aria-label={t("language")}
              data-testid="lang-toggle-group"
              className="hidden md:inline-flex items-center rounded-full bg-white border-2 border-border p-1 shadow-sm"
            >
              {LANGUAGES.map((l) => {
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    role="tab"
                    aria-selected={active}
                    data-testid={`lang-toggle-${l.code}`}
                    onClick={() => setLang(l.code)}
                    title={l.label}
                    className={`px-4 h-10 rounded-full font-bold text-sm transition-colors ${
                      active
                        ? "bg-primary text-white shadow"
                        : "text-foreground/70 hover:text-foreground"
                    }`}
                  >
                    <span className="hidden lg:inline">{l.label}</span>
                    <span className="lg:hidden">{LANG_SHORT[l.code]}</span>
                  </button>
                );
              })}
            </div>

            <AdminSettings />
            <InstallButton />
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border py-6 mt-16">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-muted-foreground">
          {t("footer")}
        </div>
      </footer>
    </div>
  );
}
